import { DatePipe, formatCurrency, formatDate } from '@angular/common';
import { MatDialog } from '@angular/material/dialog';
import { IColumnConfigTableResponsive } from '../domain/IColumnConfigTableResponsive';
import { FieldTypeEnum } from '../domain/registration/enums/field-type.enum';
import { DIALOG_CONFIG } from './global-config';
import pkg from './../../../package.json';

export class GlobalUtils {
  static isValidUUID(system: any) {
    throw new Error('Method not implemented.');
  }
  static downloadFile: any;
  public static getAppVersion() {
    return pkg.version;
  }


  public static openDialog(config: {
    dialog: MatDialog;
    component: any;
    data?: any;
    dialogConfig?: any;
    callBack?: any;
  }) {
    let data = config.data;
    let dialogConfig = data
      ? { ...DIALOG_CONFIG, ...config.dialogConfig, data }
      : DIALOG_CONFIG;

    config.dialog
      .open(config.component, dialogConfig)
      .afterClosed()
      .subscribe(config.callBack);
  }

  /**
   * Retorna a lista recebida com ordenação crescente de acordo com a key informada
   * @param array
   * @param key
   * @returns
   * @author Mauricio Linhares
   */
  public static orderAsc<T>(array: T[], key: keyof T): T[] {
    return array.slice().sort((v1: T, v2: T) => {
      if (v1[key] > v2[key]) {
        return 1;
      }

      if (v1[key] < v2[key]) {
        return -1;
      }
      return 0;
    });
  }

  /**
   * Retorna a lista recebida com ordenação decrescente de acordo com a key informada
   * @param array
   * @param key
   * @returns
   * @author Mauricio Linhares
   */
  public static orderDesc<T>(array: T[], key: keyof T): T[] {
    return array.slice().sort((v1: T, v2: T) => {
      if (v1[key] < v2[key]) {
        return 1;
      }

      if (v1[key] > v2[key]) {
        return -1;
      }
      return 0;
    });
  }
  public async getBase64Url(
    fileList: any
  ): Promise<string | ArrayBuffer | null> {
    return new Promise(async (resolve, reject) => {
      var reader = new FileReader();
      reader.onloadend = () => {
        return resolve(reader.result);
      };

      await reader.readAsDataURL(fileList[0]);
    });
  }

  /**
   * Converte base64 para Blob
   * @param image
   * @returns
   * @author Mauricio Linhares
   */
  public static async blobImageFromBase64String(base64String: any) {
    if (!base64String) return '';
    return (await fetch(base64String)).blob();
  }

  /**
   * Converte uma string base64 para Blob com o tipo MIME fornecido
   * @param base64 - String base64 sem o cabeçalho (data:image/png;base64,)
   * @param mimeType - Tipo MIME do arquivo (ex: image/png, application/pdf)
   * @returns Blob do arquivo
   */
  static async blobFromBase64String(
    base64: string,
    mimeType: string = 'application/octet-stream'
  ): Promise<Blob> {
    const byteCharacters = atob(base64);
    const byteNumbers = new Array(byteCharacters.length);

    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }

    const byteArray = new Uint8Array(byteNumbers);
    return new Blob([byteArray], { type: mimeType });
  }

  /**
   * Remoção de acentuação de palavras para filtro
   * @author Maurício Linhares
   * @param text
   * @note {parametro de validação sempre usando lowercase}
   */
  public static removeAccent(text: string) {
    text = text.toLowerCase();
    text = text.replace(new RegExp('[ÁÀÂÃ]', 'gi'), 'a');
    text = text.replace(new RegExp('[ÉÈÊ]', 'gi'), 'e');
    text = text.replace(new RegExp('[ÍÌÎ]', 'gi'), 'i');
    text = text.replace(new RegExp('[ÓÒÔÕ]', 'gi'), 'o');
    text = text.replace(new RegExp('[ÚÙÛ]', 'gi'), 'u');
    text = text.replace(new RegExp('[Ç]', 'gi'), 'c');
    return text;
  }

  public static getValueByTypeColumnTableResponsive(
    config: IColumnConfigTableResponsive,
    data: any,
    row?: any
  ) {
    // Aplicar formatter customizado da coluna quando existir
    if (config.formatter) {
      const formatted = config.formatter(data, row);
      if (formatted !== undefined) {
        data = formatted;
      }
    }
    if (data && config.displayValue) {
      data = data[config.displayValue];
    }

    if (config.field_type === FieldTypeEnum.LABEL_CURRENCY && data) {
      const numValue = Number(data);
      if (!isNaN(numValue) && isFinite(numValue)) {
        return formatCurrency(numValue, 'pt-BR', 'R$');
      } else {
        return 'R$ 0,00';
      }
    }

    if (config.field_type === FieldTypeEnum.LABEL_DATE && data)
      return formatDate(data, 'dd/MM/yyyy', 'pt-BR');

    if (config.field_type === FieldTypeEnum.LABEL_DATE_TIME && data)
      return formatDate(data, 'dd/MM/yyyy HH:mm', 'pt-BR');

    if (config.field_type === FieldTypeEnum.LABEL_PERCENTAGE && data) {
      const numValue = Number(data);
      if (!isNaN(numValue) && isFinite(numValue)) {
        if (Math.abs(Math.round(numValue) - numValue) < 0.1) {
          return `${Math.round(numValue)}%`;
        } else {
          return `${numValue.toFixed(2)}%`;
        }
      }
      return `${data}%`;
    }

    return data;
  }

  public static getCustomerDisplayName(customer: any): string {
    if (!customer) {
      return 'Cliente não identificado';
    }

    if (customer.tradingName && customer.tradingName.trim() !== '') {
      return customer.tradingName;
    }

    if (customer.companyName && customer.companyName.trim() !== '') {
      return customer.companyName;
    }

    if (customer.name && customer.name.trim() !== '') {
      return customer.name;
    }

    return 'Cliente não identificado';
  }

  public static parseCurrencyValue(value: string | number): number {
    if (typeof value === 'number') {
      return value;
    }

    if (!value || typeof value !== 'string') {
      return 0;
    }

    // Remove o prefixo R$ e espaços
    let cleanValue = value.replace(/R\$\s*/g, '').trim();

    // Substitui vírgula por ponto para conversão correta
    cleanValue = cleanValue.replace(',', '.');

    // Converte para número
    const numValue = parseFloat(cleanValue);

    // Retorna 0 se não for um número válido
    return isNaN(numValue) ? 0 : numValue;
  }

  public static formatCurrencyValue(value: number): string {
    if (value === null || value === undefined || isNaN(value)) {
      return 'R$ 0,00';
    }

    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  }

  public static calculateFees(dueDateSt:string | Date,percent:number,today:Date,value:number):number{
    const dueDate = new Date(dueDateSt);
    if(dueDate < today){
      const daysLate = Math.floor((today.getTime() - dueDate.getTime())/(1000 * 60 * 60 * 24));
      const totals =  daysLate * (value * (percent/100));
      return totals;
    }
    return 0;
  }

  static formatDate(dataISO: string): string {
    if (!dataISO) return '';

    const datePipe = new DatePipe('en-US');

    return datePipe.transform(dataISO, 'dd/MM/yyyy', 'UTC') || '';
  }
}
